import React, { createContext, useState, useEffect, useMemo } from 'react'
import JWT from 'jsonwebtoken'
import { Decoded } from '../../../Types/globalTypes'
import { sendMessageToReactNative } from '../../react-native'

interface Props {
  children: React.ReactNode
}

export const AUTH_TOKEN_KEY = 'authToken'
export const REAUTH_TOKEN_KEY = 'reAuthToken'

interface AuthContextProps {
  authToken: string | null
  reAuthToken: string | null
  login: (token: string, reAuthToken: string) => void
  logout: () => void
  valid: boolean
}

export const AuthContext = createContext<AuthContextProps>({
  authToken: null,
  reAuthToken: null,
  login: (token: string, reAuthToken: string) => {
    console.warn('login function is not implemented', token, reAuthToken)
  },
  logout: () => {
    console.warn('logout function is not implemented')
  },
  valid: false,
})

const isTokenValid = (token: string): boolean => {
  const decoded = JWT.decode(token)

  if (!decoded) return false
  return (decoded as Decoded).exp > Date.now() / 1000
}

/**
 * An authentication wrapper to provide authToken and reAuthToken, as well as functions to set (login) and remove (logout) them
 * It uses localStorage instead of redux to prevent waiting until the redux store is ready to have the tokens
 */
export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [authToken, setAuthToken] = useState(localStorage.getItem(AUTH_TOKEN_KEY))
  const [reAuthToken, setReAuthToken] = useState(localStorage.getItem(REAUTH_TOKEN_KEY))
  const [valid, setValid] = useState(false)

  /** Listen for storage changes */
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === AUTH_TOKEN_KEY) setAuthToken(event.newValue)
      if (event.key === REAUTH_TOKEN_KEY) setReAuthToken(event.newValue)
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  useEffect(() => {
    if (authToken) {
      setValid(isTokenValid(authToken))
    } else {
      setValid(false)
    }
  }, [authToken])

  /* Function to update token */
  const login = (token: string, reAuthToken: string): void => {
    localStorage.setItem(AUTH_TOKEN_KEY, token)
    localStorage.setItem(REAUTH_TOKEN_KEY, reAuthToken)

    sendMessageToReactNative({ type: 'login', authToken: token, reAuthToken: reAuthToken })

    setAuthToken(token) /* Trigger reactivity */
    setReAuthToken(reAuthToken) /* Trigger reactivity */
  }

  const logout = (): void => {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(REAUTH_TOKEN_KEY)

    sendMessageToReactNative({ type: 'logout' })

    setAuthToken(null)
    setReAuthToken(null)
  }

  const value = useMemo(
    () => ({
      authToken,
      reAuthToken,
      login,
      logout,
      valid,
    }),
    [authToken, reAuthToken, valid]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
